import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { createFileRoute, stripSearchParams, useNavigate, useRouter, useSearch } from '@tanstack/react-router'
import SFBackPageHeader from '@web/src/components/primatives/sf-back-page-header/sf-back-page-header'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import GroupForm from '@web/src/features/group/group-form/group-form'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  EditGroupByIdQuery,
  EditGroupByIdQueryVariables,
  UpdateGroupInput,
  UpdateGroupMutation,
  UpdateGroupMutationVariables,
} from 'types/graphql'
import { z } from 'zod'

const EDIT_GROUP_QUERY: TypedDocumentNode<EditGroupByIdQuery, EditGroupByIdQueryVariables> = gql`
  query EditGroupById($groupIdOrSlug: String!) {
    group: group(groupIdOrSlug: $groupIdOrSlug) {
      id
      name
      description
      tagLine
      ownerId
      visibility
      groupType
      joinApproval
      postApproval
      allowMemberEvents
      allowMemberPosts
      tags {
        id
        name
      }
    }
    groupTags {
      id
      name
    }
  }
`

const UPDATE_GROUP_MUTATION: TypedDocumentNode<UpdateGroupMutation, UpdateGroupMutationVariables> = gql`
  mutation UpdateGroupMutation($groupId: String!, $input: UpdateGroupInput!) {
    updateGroup(groupIdOrSlug: $groupId, input: $input) {
      id
      slug
      name
      description
      tagLine
      ownerId
      visibility
      groupType
      joinApproval
      postApproval
      allowMemberEvents
      allowMemberPosts
    }
  }
`

export const Route = createFileRoute('/(auth)/(main-layout)/group/$groupIdOrSlug_/edit')({
  component: EditGroupPage,
  validateSearch: z.object({
    tagFilter: z.string().optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        tagFilter: '',
      }),
    ],
  },
  head: () =>
    seo({
      title: `Edit Group | ${env.VITE_APP_NAME}`,
      description: 'Edit a Group entry',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loaderDeps: ({ search: { tagFilter } }) => ({
    tagFilter,
  }),
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, EDIT_GROUP_QUERY, {
      variables: { groupIdOrSlug: params.groupIdOrSlug },
    })
    return { queryRef }
  },
})

function EditGroupPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const router = useRouter()
  const search = useSearch({ strict: false })
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const { groupIdOrSlug } = Route.useParams()
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<EditGroupByIdQuery>)
  const isInitialLoading = networkStatus === NetworkStatus.loading

  const [updateGroup] = useMutation<UpdateGroupMutation, UpdateGroupMutationVariables>(UPDATE_GROUP_MUTATION, {
    onCompleted: (result) => {
      toast.success(t('Group updated successfully'))
      navigate({ to: '/group/$groupIdOrSlug', params: { groupIdOrSlug: result.updateGroup.slug } } as never)
      setShowSpinnerValue(false)
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
      setShowSpinnerValue(false)
    },
  })

  const onSave = (input: UpdateGroupInput) => {
    setShowSpinnerValue(true)
    updateGroup({
      variables: {
        groupId: data?.group?.id || groupIdOrSlug,
        input,
      },
    })
  }

  const handleCancel = () => {
    router.history.back()
  }

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  const group = data?.group ?? null
  const groupTags = data?.groupTags ?? []

  // Filter groupTags based on search parameter if provided
  const filteredGroupTags = search.tagFilter
    ? groupTags.filter((tag) => tag.name.toLowerCase().includes(search.tagFilter?.toLowerCase() ?? ''))
    : groupTags

  if (!group) {
    return <ErrorAlert error={new Error(t('Group not found'))} />
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 px-2 pt-4">
      <SFBackPageHeader lowerText={group.name} onBackClick={handleCancel} title={t('Edit Group')} />
      <GroupForm group={group} groupTags={filteredGroupTags} onSave={onSave} showSpinner={showSpinner} />
    </div>
  )
}
